import { defineStore } from 'pinia'
import api from '@/services/api'
import { isRelevanceInRange } from '@/services/relevance'
import { setError } from '@/mixins/events'

export const useSearchStore = defineStore('search', {
  state: () => ({
    // Search query
    query: '',
    // Search results
    results: [],
    // Loading state
    loading: false,
    // Error state
    error: null,
    // Deep search flag
    deepSearch: false,
    // Filters
    filters: {
      keywords: [],
      source: null,
      licenses: [],
      formats: [],
      relevanceMin: null,
      relevanceMax: null,
      sizes: [], // Array di ordini di grandezza: 'byte', 'kb', 'mb', 'gb'
    },

    // Saved datasets (temporary, per search session)
    savedDatasets: [],
    // Current view: 'search' or 'saved'
    currentView: 'search',
  }),

  getters: {
    /**
     * Get filtered results based on active filters
     */
    filteredResults: (state) => {
      let filtered = state.results

      // Filter by source
      if (state.filters.source) {
        filtered = filtered.filter(
          (result) => result.source === state.filters.source
        )
      }

      // Filter by licenses (multiple selection)
      if (state.filters.licenses.length > 0) {
        filtered = filtered.filter(
          (result) => state.filters.licenses.includes(result.license)
        )
      }

      // Filter by formats (multiple selection)
      if (state.filters.formats.length > 0) {
        filtered = filtered.filter((result) =>
          result.files?.some((file) => state.filters.formats.includes(file.format))
        )
      }

      // Filter by relevance score (numeric or categorical)
      if (state.filters.relevanceMin !== null || state.filters.relevanceMax !== null) {
        filtered = filtered.filter((result) =>
          isRelevanceInRange(
            result.max_relevance_score,
            state.filters.relevanceMin,
            state.filters.relevanceMax
          )
        )
      }

      // Filter by individual file size (at least one file must match selected size ranges)
      if (state.filters.sizes.length > 0) {
        filtered = filtered.filter((result) => {
          return result.files?.some((file) => {
            const fileSize = file.file_size || 0
            return state.filters.sizes.some((sizeRange) => {
              switch (sizeRange) {
                case 'byte':
                  return fileSize < 1024 // < 1 KB
                case 'kb':
                  return fileSize >= 1024 && fileSize < 1024 * 1024 // 1 KB - 1 MB
                case 'mb':
                  return fileSize >= 1024 * 1024 && fileSize < 1024 * 1024 * 1024 // 1 MB - 1 GB
                case 'gb':
                  return fileSize >= 1024 * 1024 * 1024 // >= 1 GB
                default:
                  return false
              }
            })
          })
        })
      }

      // Filter by keywords
      if (state.filters.keywords.length > 0) {
        filtered = filtered.filter((result) => {
          const resultText = `${result.title} ${result.description}`.toLowerCase()
          return state.filters.keywords.some((keyword) =>
            resultText.includes(keyword.toLowerCase())
          )
        })
      }

      return filtered
    },

    /**
     * Get unique sources from results
     */
    sourcesFromResults: (state) => {
      const sources = new Set()
      state.results.forEach((result) => {
        if (result.source) sources.add(result.source)
      })
      return Array.from(sources).sort()
    },

    /**
     * Get unique licenses from results
     */
    licensesFromResults: (state) => {
      const licenses = new Set()
      state.results.forEach((result) => {
        if (result.license) licenses.add(result.license)
      })
      return Array.from(licenses).sort()
    },

    /**
     * Get unique file formats from results
     */
    formatsFromResults: (state) => {
      const formats = new Set()
      state.results.forEach((result) => {
        result.files?.forEach((file) => {
          if (file.format) formats.add(file.format)
        })
      })
      return Array.from(formats).sort()
    },

    /**
     * Check if there are active filters
     */
    hasActiveFilters: (state) => {
      return (
        state.filters.source ||
        state.filters.licenses.length > 0 ||
        state.filters.formats.length > 0 ||
        state.filters.keywords.length > 0 ||
        state.filters.relevanceMin !== null ||
        state.filters.relevanceMax !== null ||
        state.filters.sizes.length > 0
      )
    },

    /**
     * Get total number of results
     */
    totalResults: (state) => state.results.length,

    /**
     * Check if search has been performed
     */
    hasSearched: (state) => state.query !== '',

    /**
     * Check if a dataset is saved
     */
    isDatasetSaved: (state) => (datasetUrl) => {
      return state.savedDatasets.some((d) => d.dataset_url === datasetUrl)
    },

    /**
     * Get count of saved datasets
     */
    savedDatasetsCount: (state) => state.savedDatasets.length,
  },

  actions: {
    /**
     * Search for datasets
     * @param {string} query - Search query
     * @param {boolean} deepSearch - Enable deep search
     */
    async searchDatasets(query, deepSearch = false) {
      if (!query || !query.trim()) {
        this.error = 'Search query cannot be empty'
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await api.searchDatasets(query, {
          deepSearch,
          keywords: this.filters.keywords,
          source: this.filters.source,
          license: this.filters.license,
        })

        this.results = response.data.results || []
        this.query = query
        this.deepSearch = deepSearch
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Search failed'
        this.results = []
        console.error('Search error:', error)
        setError(error, false);

      } finally {
        this.loading = false
      }
    },

    /**
     * Clear search results
     */
    clearResults() {
      this.results = []
      this.query = ''
      this.error = null
      this.deepSearch = false
    },

    /**
     * Set filter value
     * @param {string} filterName - Name of the filter
     * @param {*} value - Filter value
     */
    setFilter(filterName, value) {
      if (filterName in this.filters) {
        this.filters[filterName] = value
      }
    },

    /**
     * Clear all filters
     */
    clearFilters() {
      this.filters = {
        keywords: [],
        source: null,
        licenses: [],
        formats: [],
        relevanceMin: null,
        relevanceMax: null,
        sizes: [],
      }
    },

    /**
     * Clear a specific filter
     * @param {string} filterName - Name of the filter to clear
     */
    clearFilter(filterName) {
      if (filterName in this.filters) {
        if (Array.isArray(this.filters[filterName])) {
          this.filters[filterName] = []
        } else {
          this.filters[filterName] = null
        }
      }
    },

    /**
     * Set deep search flag
     * @param {boolean} value - Deep search enabled/disabled
     */
    setDeepSearch(value) {
      this.deepSearch = value
    },

    /**
     * Toggle dataset saved state
     * @param {object} dataset - Dataset to save/unsave
     */
    toggleSavedDataset(dataset) {
      const index = this.savedDatasets.findIndex((d) => d.dataset_url === dataset.dataset_url)
      if (index > -1) {
        this.savedDatasets.splice(index, 1)
      } else {
        this.savedDatasets.push(dataset)
      }
    },

    /**
     * Clear all saved datasets
     */
    clearSavedDatasets() {
      this.savedDatasets = []
    },

    /**
     * Set current view
     * @param {string} view - 'search' or 'saved'
     */
    setCurrentView(view) {
      this.currentView = view
    },
  },
})
